import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../services/api/axios";
import { FormModal } from "./form";
import { ViewButton } from "./Buttons";

/**
 * Read-only view of the cost-sheet approval history for a single property.
 * Two FormModal shells: the outer history list, and a nested "view content"
 * modal that renders the cost-sheet HTML for a chosen approval.
 */
const CostSheetHistoryModal = ({ isOpen, onClose, property, userInfo }) => {
    const [costSheetHistory, setCostSheetHistory] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [selectedCostSheetContent, setSelectedCostSheetContent] = useState("");
    const [selectedCostSheetTitle, setSelectedCostSheetTitle] = useState("");

    const fetchCostSheetHistory = useCallback(async () => {
        if (!property?.propertyData || !userInfo) return;
        const customerId = userInfo?.loginIdentifierDetails?.customer?.customerId;
        if (!customerId) return;
        try {
            setIsLoading(true);
            const response = await axiosInstance.get("/approvals", {
                params: {
                    customerId,
                    propertyId: property.propertyData.propertyId,
                },
            });
            const costSheetApprovals = Array.isArray(response)
                ? response.filter((a) =>
                      ["COST_SHEET", "Cost Sheet", "COST_SHEET_APPROVAL"].includes(
                          a.approvalType?.approvalTypeName,
                      ),
                  )
                : [];
            setCostSheetHistory(
                costSheetApprovals.map((a) => ({
                    approvalId: a.approvalId,
                    propertyId: property.propertyData.propertyId,
                    propertyNo: property.propertyData.propertyNo,
                    buildingNo: property.propertyData.buildingNo,
                    projectName:
                        property.propertyData.projectPhase?.project?.projectName || "N/A",
                    propertyType: property.propertyData.propertyType,
                    leadName:
                        a.user?.firstName && a.user?.lastName
                            ? `${a.user.firstName} ${a.user.lastName}`
                            : a.user?.firstName || a.user?.lastName || "N/A",
                    leadMobile: a.user?.mobileNoWa || a.user?.mobileNoCalling || "N/A",
                    leadEmail: a.user?.emailId || "N/A",
                    costSheetContent: a.attachment || a.content || "N/A",
                    sentDate: a.approvalDatetime || a.createdDate || "N/A",
                    status: a.status || "PENDING",
                    notificationSent: a.isNotificationSent || false,
                })),
            );
        } catch (error) {
            console.error("Error fetching cost sheet history:", error);
            toast.error("Failed to fetch cost sheet history");
        } finally {
            setIsLoading(false);
        }
    }, [property, userInfo]);

    useEffect(() => {
        if (isOpen && property) fetchCostSheetHistory();
        if (!isOpen) setCostSheetHistory([]);
    }, [isOpen, property, fetchCostSheetHistory]);

    const handleOpenView = (content, title) => {
        setSelectedCostSheetContent(content);
        setSelectedCostSheetTitle(title);
        setIsViewModalOpen(true);
    };
    const handleCloseView = () => {
        setIsViewModalOpen(false);
        setSelectedCostSheetContent("");
        setSelectedCostSheetTitle("");
    };

    const propMeta = property?.propertyData;
    const subtitle = propMeta
        ? `${propMeta.projectPhase?.project?.projectName || "N/A"} - ${propMeta.propertyType || "N/A"} (Property: ${propMeta.propertyNo || "N/A"} | Building: ${propMeta.buildingNo || "N/A"})`
        : null;

    const hasApprovedRow = costSheetHistory.some((item) => item.status === "APPROVED");

    return (
        <>
            <FormModal
                open={isOpen}
                onClose={onClose}
                title="Cost Sheet History"
                size="xl"
            >
                {subtitle && (
                    <p className="text-sm font-normal text-gray-600 mb-4 -mt-2">{subtitle}</p>
                )}

                {isLoading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-flamingo-600 mx-auto mb-4" />
                            <p className="text-gray-600">Loading cost sheet history...</p>
                        </div>
                    </div>
                ) : costSheetHistory.length === 0 ? (
                    <div className="text-center py-12">
                        <p className="text-gray-500 text-lg">
                            No cost sheet history found for this property
                        </p>
                        <p className="text-gray-400 text-sm mt-2">
                            Cost sheets sent to leads will appear here
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                                        Lead Information
                                    </th>
                                    {hasApprovedRow && (
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                                            Approval Date
                                        </th>
                                    )}
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                                        Status
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                                        Notification
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {costSheetHistory.map((item, index) => (
                                    <tr key={`${item.approvalId}-${index}`} className="hover:bg-gray-50">
                                        <td className="px-4 py-4 border-b">
                                            <div className="space-y-1">
                                                <div className="font-medium text-gray-900">{item.leadName}</div>
                                                <div className="text-sm text-gray-500">{item.leadMobile}</div>
                                                <div className="text-sm text-gray-500">{item.leadEmail}</div>
                                            </div>
                                        </td>
                                        {hasApprovedRow && (
                                            <td className="px-4 py-4 border-b">
                                                {item.status === "APPROVED" ? (
                                                    <>
                                                        <div className="text-sm text-gray-900">
                                                            {new Date(item.sentDate).toLocaleDateString()}
                                                        </div>
                                                        <div className="text-xs text-gray-500">
                                                            {new Date(item.sentDate).toLocaleTimeString()}
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div className="text-sm text-gray-500 italic">
                                                        Pending
                                                    </div>
                                                )}
                                            </td>
                                        )}
                                        <td className="px-4 py-4 border-b">
                                            <span
                                                className={`inline-flex px-3 py-2 text-xs font-semibold rounded-full ${
                                                    item.status === "APPROVED"
                                                        ? "bg-green-100 text-green-800"
                                                        : item.status === "REJECTED"
                                                            ? "bg-red-100 text-red-800"
                                                            : item.status === "PENDING"
                                                                ? "bg-yellow-100 text-yellow-800"
                                                                : "bg-gray-100 text-gray-800"
                                                }`}
                                            >
                                                {item.status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 border-b">
                                            <span
                                                className={`inline-flex px-3 py-2 text-sm font-medium ${
                                                    item.notificationSent
                                                        ? "text-blue-700"
                                                        : "text-red-800"
                                                }`}
                                            >
                                                {item.notificationSent ? "Sent" : "Not Sent"}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 border-b">
                                            <ViewButton
                                                onClick={() => {
                                                    if (
                                                        item.costSheetContent &&
                                                        item.costSheetContent !== "N/A"
                                                    ) {
                                                        handleOpenView(
                                                            item.costSheetContent,
                                                            `Cost Sheet - ${item.projectName} (${item.propertyNo})`,
                                                        );
                                                    } else {
                                                        toast.info("Cost sheet content not available");
                                                    }
                                                }}
                                            >
                                                View
                                            </ViewButton>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </FormModal>

            {/* Nested view modal — renders the cost-sheet HTML for a chosen approval */}
            <FormModal
                open={isViewModalOpen}
                onClose={handleCloseView}
                title={selectedCostSheetTitle}
                size="xl"
            >
                <div
                    className="overflow-auto"
                    dangerouslySetInnerHTML={{ __html: selectedCostSheetContent }}
                />
            </FormModal>
        </>
    );
};

export default CostSheetHistoryModal;
